import * as React from "react";
import { AddFilterBaseFunc, IFilterKeys, IFilters, IPlotData, RemoveFilterBaseFunc } from "models/core";
import { PlotData } from "plotly.js";
import { CircularProgress, Dialog, Grid } from "@mui/material";
import { addFilterFactory } from "helpers/filterHelper";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { SimpleBreadcrumb } from "components/common/SimpleBreadcrumb";
import watermark from "../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";
import { PrimarySiteHierarchy } from "models/mdt/plotdata";
import { AppContext } from "AppContext";
import { Person } from "@mui/icons-material";

export function chartDimesionsCalc(chartHeightProportion: number, parentColNumber?: number, tabColNumber?: number) {

	const parentColNumberP = 12 / parentColNumber;
	const tabColNumberP = (12 / tabColNumber) * parentColNumberP || 2;

	return {
		//paperHeight: Math.round(window.innerWidth > 991 ? (window.innerHeight-230)/(100/chartHeightProportion) : (window.innerHeight-230)/(100/chartHeightProportion)*2 ),
		paperHeight: (window.innerWidth > 991 ? 400 : 800),
		paperWidth: Math.round((window.innerWidth > 991 ? window.innerWidth / (tabColNumberP * 1.2) : (window.innerWidth - 100) * 0.85)),
		chartWidth: Math.round((window.innerWidth > 991 ? window.innerWidth / (tabColNumberP * 1.2) * 0.97 : (window.innerWidth - 100) * 0.75)),
		//chartHeight: Math.round(window.innerWidth > 991 ? ((window.innerHeight-230)/(100/chartHeightProportion))*0.8 : ((window.innerHeight-230)/(100/chartHeightProportion))*0.8*2),
		chartHeight: (window.innerWidth > 991 ? 330 : 550),
		middleChart: Math.round(((window.innerHeight - 230) / (100 / chartHeightProportion)) / 2.5),
		//middleChartText: Math.round(window.innerWidth > 991 ? ((window.innerHeight-230)/(100/chartHeightProportion))/8 : ((window.innerHeight-230)/(100/chartHeightProportion))/2),
		middleChartText: (window.innerWidth > 991 ? 130 : 300),
		fullScreenPaperHeight: Math.round(window.innerHeight - 300),
		fullScreenChartHeight: Math.round((window.innerHeight - 300) * 0.85),
		fullScreenChartWidth: Math.round((window.innerWidth - 100) * 0.8),
		fullScreenTextSize: Math.round(window.innerWidth > 991 ? (window.innerWidth) / 80 : 15),
		textSize: Math.round(window.innerWidth > 991 ? (window.innerWidth) / 120 : 15)
	};
}


type Pd =
	PrimarySiteHierarchy.IHierarchyPlotData
	| IPlotData[];

interface IProps<T> {
	plotData: T;
	activeFilters: IFilters<IFilterKeys>;
	addFilterFn: AddFilterBaseFunc<IFilterKeys>;
	removeFilterFn: RemoveFilterBaseFunc<IFilterKeys>;
	defaultChartType: ChartType;
	defaultChartOrientation: ChartOrientation;
	filterKey: string;
	chartTitle: string;
	chartOrder: IChartOrder;
	xAxis: IChartAxis;
	yAxis: IChartAxis;
	hoverText: string;
	hovertextFn?: (values: number[]) => string[];
	textFn?: (values: number[]) => string[];
	filterFn?: (chartType: ChartType, chartOrientation: ChartOrientation) => Function;
	clipOnAxis?: boolean;

	// optional props
	chartDimensions?: IChartDimensions;
	calculateRangeOnPlotDataIndex?: number;
	chartIcons?: IChartIcons;
	debug?: boolean;
	summary?: JSX.Element;
	legend?: (chartType: ChartType) => JSX.Element;
	sortUnknownToEnd?: boolean;
	breadcrumb?: JSX.Element;
	msgWhenHidden?: string;
	hoverTextAlt?: string[];
	hovertemplate?: string;
}

interface IChartIcons {
	verticalBar?: boolean;
	horizontalBar?: boolean;
	table?: boolean;
	line?: boolean;
	pie?: boolean;
	person?: boolean;
	downloadAsCsv?: boolean;
	downloadAsPng?: boolean;
	fullScreen?: boolean;
	help?: string[];
}

interface IChartDimensions {
	chartHeightProportion: number;
	parentColNumber?: number;
	tabColNumber?: number;
}

interface IChartAxis {

	verticalOrientation: {
		label: string;
		style?: Partial<Plotly.LayoutAxis>
	};
	horizontalOrientation: {
		label: string;
		style?: Partial<Plotly.LayoutAxis>
	};
}

interface IChartOrder {
	verticalOrientation: {
		reversed: boolean;
	};
	horizontalOrientation: {
		reversed: boolean;
	}

}

const getLabelOrder = (co: ChartOrientation, chartOrder: IChartOrder, labels: string[]) => {
	switch (co) {
		case ChartOrientation.Horizontal:
			if (chartOrder.horizontalOrientation.reversed) {
				return labels.slice().reverse();
			} else {
				return labels;
			}


		case ChartOrientation.Vertical:
			if (chartOrder.verticalOrientation.reversed) {
				return labels.slice().reverse();
			} else {
				return labels;
			}
	}
}
const getValueOrder = (co: ChartOrientation, chartOrder: IChartOrder, values: number[]) => {
	switch (co) {
		case ChartOrientation.Horizontal:
			if (chartOrder.horizontalOrientation.reversed) {
				return values.slice().reverse();
			} else {
				return values;
			}


		case ChartOrientation.Vertical:
			if (chartOrder.verticalOrientation.reversed) {
				return values.slice().reverse();
			} else {
				return values;
			}
	}
}


const getAxisLabel = (co: ChartOrientation, axis: IChartAxis) => {
	switch (co) {
		case ChartOrientation.Horizontal:
			return axis.horizontalOrientation.label;

		default:
			return axis.verticalOrientation.label;
	}
}


export enum ChartType {
	Bar,
	Pie,
	Table,
	Line,
	Person
}

const getChartType = (ct: ChartType) => {
	switch (ct) {
		case ChartType.Bar:
			return "bar";
		case ChartType.Pie:
			return "pie";
		case ChartType.Table:
			return "table";
		case ChartType.Line:
			return "scatter"
		case ChartType.Person:
			return "person"
		default:
			return "bar";
	}
}

const toChartType = (ct: string) => {
	switch (ct) {
		case "pie":
			return ChartType.Pie;
		case "bar":
			return ChartType.Bar;
		case "table":
			return ChartType.Table;
		case "scatter":
			return ChartType.Line;
		case "person":
			return ChartType.Person;
		default:
			return ChartType.Bar;

	}
}

export enum ChartOrientation {
	Horizontal,
	Vertical
}

const toChartOrientation = (co: string) => {
	switch (co) {
		case "h":
			return ChartOrientation.Horizontal;

		case "v":
			return ChartOrientation.Vertical

		default:
			return ChartOrientation.Horizontal;
	}
}

const getChartOrientation = (co: ChartOrientation) => {
	switch (co) {
		case ChartOrientation.Horizontal:
			return "h";
		case ChartOrientation.Vertical:
			return "v";
		default:
			return "v";
	}
}

export const textTemplate = (chartType: ChartType, chartOrientation: ChartOrientation) => {
	let result = "%{text}";

	if (chartType == ChartType.Pie)
		result = result + "<br>%{label}";

	return result;
}

export const UNKNOWN_TERMS = [
	"Unk",
	"Unknown",
	"Not Determined",
	"No Treatment/Unk",
	"No Tx/Unk",
	"Had no treatment",
	"undefined"
];

export const text = (chartType: ChartType, chartOrientation: ChartOrientation, values: number[]): string[] => {
	if (chartType == ChartType.Line)
		return values.map(a => `${a}%`)

	const d = values.reduce((a, b) => (a || 0) + (b || 0), 0);

	if (isNaN(d)) {
		return []
	}

	let result = values.map(x => {
		if (x == undefined || d == undefined)
			return `0 (0%)`;

		if (d == 0)
			return `${x} (0%)`;

		const c = (x / d * 100);

		if (isNaN(c))
			return `${x} (${d})`

		//Add line for pie to put % in its own line 
		if (chartType == ChartType.Pie)
			return `${x} <br> (${c.toFixed(1)}%)`

		return `${x} (${c.toFixed(1)}%)`
	});

	return result;
}

export const SortUnknownToEnd = (plotData: IPlotData): IPlotData => {
	const unkIndex = plotData.labels.findIndex(a => UNKNOWN_TERMS.indexOf(a) > -1);

	if (unkIndex == -1)
		return plotData;

	const unkLabel = plotData.labels[unkIndex];
	const unkValue = plotData.values[unkIndex];

	plotData.values.splice(unkIndex, 1)
	plotData.labels.splice(unkIndex, 1);

	plotData.values.unshift(unkValue);
	plotData.labels.unshift(unkLabel);

	return {
		values: plotData.values,
		labels: plotData.labels,
		name: plotData.name
	};
}

export const QoolDashChart = (props: IProps<any>) => {
	const [chartType, setChartType] = React.useState(props.defaultChartType);
	const [chartOrientation, setChartOrientation] = React.useState(props.defaultChartOrientation);
	const [fullScreenOpen, setFullScreenOpen] = React.useState(false);

	const { state, dispatch } = React.useContext(AppContext);

	const { textFn, plotData, activeFilters, addFilterFn, removeFilterFn, filterKey, chartTitle, xAxis, yAxis, chartOrder, chartDimensions, hoverText } = props;

	let breadcrumb = null;
	if (props.breadcrumb) {
		breadcrumb = props.breadcrumb
	} else {
		breadcrumb = <SimpleBreadcrumb filteredValues={activeFilters[filterKey]} filterKey={filterKey} removeFilter={removeFilterFn} />
	}

	const chartStyle = { padding: 0, margin: 10, height: "100%", minHeight: 500 };
	const centerChartContentStyle = { display: "flex" as const, alignItems: "center" as const, justifyContent: "center" as const, textAlign: "center" as const };

	const dimensions = chartDimesionsCalc(chartDimensions?.chartHeightProportion ?? 50, chartDimensions?.parentColNumber ?? 12, chartDimensions?.tabColNumber ?? 3);


	if (state.page.computing) {

		return (
			<div className={"paper"} style={{
				float: "right",
				width: "100%",
				height: "100%",
				padding: "12px",
			}}>
				<DashChart chartKey={chartTitle} heading={chartTitle} breadcrumb={breadcrumb} style={chartStyle}>
					<div style={{ height: dimensions.chartHeight, textAlign: "center", position: "relative", top: 100 }}><CircularProgress /></div>
					{props.legend && props.legend(chartType)}
				</DashChart>
			</div>
		);
	}


	const pdIndex =
		props.calculateRangeOnPlotDataIndex && plotData.length > props.calculateRangeOnPlotDataIndex
			? props.calculateRangeOnPlotDataIndex
			: 0;

	const filterFn =
		props.filterFn && props.filterFn(chartType, chartOrientation) ||
		addFilterFactory(addFilterFn, filterKey,
			(chartType == ChartType.Pie
				? p => p.data.labels[p.pointNumber].toString()
				: chartOrientation == ChartOrientation.Horizontal
					? p => p.y.toString()
					: p => p.x.toString()
			))

	const traces: Partial<PlotData>[] = [];
	const logo = watermark;

	const chartData = {
		orientiation: getChartOrientation(chartOrientation),
		xLabel: getAxisLabel(chartOrientation, xAxis),
		yLabel: getAxisLabel(chartOrientation, yAxis),
		type: getChartType(chartType),
		chartHeight: fullScreenOpen ? dimensions.fullScreenChartHeight : dimensions.chartHeight,
		chartWidth: fullScreenOpen ? dimensions.fullScreenChartWidth : dimensions.chartWidth,
		paperHeight: fullScreenOpen ? dimensions.fullScreenPaperHeight : dimensions.paperHeight,
		paperWidth: fullScreenOpen ? dimensions.fullScreenChartWidth : dimensions.paperWidth,
		textSize: fullScreenOpen ? dimensions.fullScreenTextSize : dimensions.textSize,
		getValues: (v: number[]) => getValueOrder(chartOrientation, chartOrder, v),
		getLabels: (l: string[]) => getLabelOrder(chartOrientation, chartOrder, l),

	};


	const labels = chartData.getLabels(plotData[pdIndex].labels);

	(plotData || []).forEach((a: IPlotData) => {
		let data: IPlotData = a;
		if (props.sortUnknownToEnd) {
			data = SortUnknownToEnd(a);
		}


		const values = chartData.getValues(data.values);



		if (chartType == ChartType.Pie) {
			traces.push({
				values: values,
				name: data.name,
				labels: labels,
				text: textFn && textFn(values) || text(chartType, chartOrientation, values),
				texttemplate: textTemplate(chartType, chartOrientation),
				hoverinfo: "none",
				hole: 0.6,
				type: chartData.type,
				showlegend: false,
				direction: "clockwise",
				insidetextorientation: "horizontal",
				textposition: "auto",
				cliponaxis: props.clipOnAxis != undefined ? props.clipOnAxis : false,
				marker: {},
				sort: false,
			} as Partial<Plotly.PlotData>)
		}
		else if (chartOrientation == ChartOrientation.Vertical) {
			traces.push({
				x: labels,
				y: values,
				name: data.name,
				showlegend: false,
				text: textFn && textFn(values) || text(chartType, chartOrientation, values),
				texttemplate: textTemplate(chartType, chartOrientation),
				textposition: "top center",
				cliponaxis: props.clipOnAxis != undefined ? props.clipOnAxis : false,
				textfont: {
					size: 12,
				},
				hovertext: props.hoverTextAlt || labels,
				hovertemplate: props.hovertemplate || `%{hovertext}<br>${hoverText}: %{text}<br><extra></extra>`,
				type: chartData.type,
				orientation: chartData.orientiation,
				marker: {},

			} as Partial<Plotly.PlotData>)
		}
		else if (chartOrientation == ChartOrientation.Horizontal) {
			traces.push({
				x: values,
				y: labels,
				name: data.name,
				showlegend: false,
				text: textFn && textFn(values) || text(chartType, chartOrientation, values),
				texttemplate: textTemplate(chartType, chartOrientation),
				textposition: "top center",
				cliponaxis: props.clipOnAxis != undefined ? props.clipOnAxis : false,
				textfont: {
					size: 12,
				},
				hovertext: props.hoverTextAlt || labels,
				hovertemplate: props.hovertemplate || `%{hovertext}<br>${hoverText}: %{text}<br><extra></extra>`,
				type: chartData.type,
				orientation: chartData.orientiation,
				marker: {},

			} as Partial<Plotly.PlotData>)
		}
	});




	let layout: Partial<Plotly.Layout> = {

	}

	if (yAxis.horizontalOrientation.style && ChartOrientation.Horizontal == chartOrientation) {
		layout.yaxis = Object.assign({}, layout.yaxis, yAxis.horizontalOrientation.style)
	}

	if (yAxis.verticalOrientation.style && ChartOrientation.Vertical == chartOrientation) {
		layout.yaxis = Object.assign({}, layout.yaxis, yAxis.verticalOrientation.style)
	}

	if (xAxis.horizontalOrientation.style && ChartOrientation.Horizontal == chartOrientation) {
		layout.xaxis = Object.assign({}, layout.xaxis, xAxis.horizontalOrientation.style)
	}

	if (xAxis.verticalOrientation.style && ChartOrientation.Vertical == chartOrientation) {
		layout.xaxis = Object.assign({}, layout.xaxis, xAxis.verticalOrientation.style)
	}

	if (chartType == ChartType.Pie) {
		layout.margin = { l: 60, r: 60, b: 40, t: 40, pad: 2 };
	}

	const showChart = plotData[pdIndex].values.length > 0;

	const chart = (width: number) =>
		CreateChart(chartData.chartHeight,
			width,
			chartData.paperHeight,
			chartData.orientiation,
			chartData.type,
			showChart,
			chartData.xLabel,
			chartData.yLabel,
			plotData[pdIndex].labels,
			plotData[pdIndex].values,
			traces,
			filterFn,
			layout,
			props.debug);


	const chartIcons =
		<ChartIcons
			vbar={props.chartIcons?.verticalBar ?? true}
			hbar={props.chartIcons?.horizontalBar ?? true}
			table={props.chartIcons?.table ?? true}
			scatter={props.chartIcons?.line ?? true}
			pie={props.chartIcons?.pie ?? true}
			person={props.chartIcons?.person ?? false}
			downloadAsCsv={props.chartIcons?.downloadAsCsv ?? true}
			downloadAsPng={props.chartIcons?.downloadAsPng ?? true}
			isFullScreen={fullScreenOpen}
			viewAsFullScreen={props.chartIcons?.fullScreen ?? true}
			tooltipInfo={props.chartIcons?.help != undefined}
			tooltipText={props.chartIcons?.help != undefined ? props.chartIcons.help : [""]}
			chartOrientation={chartData.orientiation}
			chartType={chartData.type}
			traces={traces}
			xLabel={chartData.xLabel}
			yLabel={chartData.yLabel}
			title={chartTitle}
			setChartOrientation={(a: string) => setChartOrientation(toChartOrientation(a))}
			setChartType={(a: string) => setChartType(toChartType(a))}
			setFullScreen={setFullScreenOpen}
			currentFilters={activeFilters}
		/>



	let chartGrid = null;
	if (props.summary) {
		chartGrid = (
			<Grid container>
				<Grid item xs={4} style={{ fontSize: chartData.textSize * 0.7, marginTop: 10 }}>{props.summary}</Grid>
				<Grid item xs={8}>{chart(chartData.chartWidth * 0.7)}</Grid>
			</Grid>
		)
	} else {
		chartGrid = (
			<Grid container>
				<Grid item xs={12}>{chart(chartData.chartWidth)}</Grid>
			</Grid>
		)
	}

	if (fullScreenOpen) {
		return (
			<Dialog
				open={fullScreenOpen}
				fullWidth={true}
				maxWidth={false}
				style={{
					width: (window.innerWidth - 100),
					height: ((chartData.chartHeight * 1.8)),
					marginLeft: 50,
					marginRight: 50
				}}
			>
				<DashChart style={chartStyle} heading={chartTitle} chartKey={chartTitle}>
					<div style={{ marginTop: "-70px", float: "right", display: "inline" }}>
						{chartIcons}
						<br />
						{<img src={logo} alt="Cancer Alliance Queensland" width={200} style={{ marginTop: "20px" }} />}
					</div>
					<div style={{ marginLeft: "50px" }}>
						<div style={centerChartContentStyle}>
							{chartGrid}
							<div style={{ minWidth: "120px", marginTop: chartType == ChartType.Bar ? 20 : 0 }}>{props.legend && props.legend(chartType)}</div>
						</div>
					</div>
				</DashChart>
			</Dialog>
		);
	}

	return (
		<div>
			<div className={"paper"} key={chartTitle} style={{ float: "right", width: "100%", height: "100%", padding: "12px" }}>
				{chartIcons}
			</div>
			<DashChart chartKey={chartTitle} heading={chartTitle} breadcrumb={breadcrumb} style={chartStyle}>
				{
					<div style={centerChartContentStyle}>
						<div>
							{chartGrid}

							<div style={{ marginTop: chartType == ChartType.Bar ? 20 : 0 }}>{props.legend && props.legend(chartType)}</div>

							{!showChart && props.msgWhenHidden &&
								<div style={{ width: "100%", clear: "both", position: "relative", top: 80, textAlign: "center" }}>
									{props.msgWhenHidden}
								</div>
							}
						</div>
					</div>
				}
			</DashChart>
		</div>
	);

}


